/* eslint-disable @next/next/no-img-element */
"use client";

import * as React from "react";
import type { Editor } from "@tiptap/core";
import { Fragment, type Node as PMNode } from "@tiptap/pm/model";
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
} from "@dnd-kit/core";
import {
    restrictToParentElement,
    restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocale } from "@/hooks/useLocale";
import { t } from "@/lib/localization";

export interface ReorderItemMeta {
    label: string;
    thumbnail?: string | null;
}

interface BlockItemsReorderProps {
    editor: Editor;
    getPos: () => number | undefined;
    /** The block node whose direct children are the reorderable items. */
    node: PMNode;
    getItemMeta: (item: PMNode, index: number) => ReorderItemMeta;
    /** Called after a successful reorder (e.g. to clear a stale selection). */
    onReorder?: () => void;
    /** When provided, each row shows a delete button removing that item. */
    onDeleteItem?: (index: number) => void;
}

/** Move the block's child at `from` to `to` in a single transaction so the
 *  reorder is one undo step and triggers a normal editor update (autosave). */
function moveChild(
    editor: Editor,
    getPos: () => number | undefined,
    node: PMNode,
    from: number,
    to: number
) {
    const pos = getPos();
    if (pos == null || from === to) return;
    const items: PMNode[] = [];
    node.forEach((child) => items.push(child));
    if (
        from < 0 ||
        to < 0 ||
        from >= items.length ||
        to >= items.length
    )
        return;
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    const start = pos + 1;
    const end = pos + node.nodeSize - 1;
    const tr = editor.state.tr.replaceWith(start, end, Fragment.fromArray(items));
    editor.view.dispatch(tr);
}

function SortableRow({
    id,
    meta,
    onDelete,
}: {
    id: string;
    meta: ReorderItemMeta;
    onDelete?: () => void;
}) {
    const { locale } = useLocale();
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            className={cn(
                "flex items-center gap-2 rounded-md border bg-background px-2 py-1.5",
                isDragging && "relative z-10 shadow-sm opacity-90"
            )}
        >
            <button
                type="button"
                aria-label={t("lomda.scene-block.drag-handle", locale)}
                className="-ms-1 inline-flex shrink-0 cursor-grab touch-none items-center text-muted-foreground hover:text-foreground active:cursor-grabbing"
                {...attributes}
                {...listeners}
            >
                <GripVertical className="size-4" />
            </button>
            {meta.thumbnail !== undefined ? (
                <span className="size-7 shrink-0 overflow-hidden rounded border bg-muted">
                    {meta.thumbnail ? (
                        <img
                            src={meta.thumbnail}
                            alt=""
                            className="size-full object-cover"
                        />
                    ) : null}
                </span>
            ) : null}
            <span
                className={cn(
                    "min-w-0 flex-1 truncate text-xs",
                    meta.label
                        ? "text-foreground"
                        : "text-muted-foreground italic"
                )}
            >
                {meta.label || t("lomda.scene-block.untitled", locale)}
            </span>
            {onDelete ? (
                <button
                    type="button"
                    aria-label={t("lomda.scene-block.remove", locale)}
                    onClick={onDelete}
                    className="-me-1 inline-flex shrink-0 items-center rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                    <Trash2 className="size-3.5" />
                </button>
            ) : null}
        </div>
    );
}

/** Drag-and-drop list of a block's items, rendered inside its settings popover.
 *  Reordering rewrites the block's children in the document. */
export function BlockItemsReorder({
    editor,
    getPos,
    node,
    getItemMeta,
    onReorder,
    onDeleteItem,
}: BlockItemsReorderProps) {
    const { locale } = useLocale();
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
    );

    const items: { id: string; index: number; meta: ReorderItemMeta }[] = [];
    node.forEach((child, _offset, index) => {
        items.push({
            id: String(index),
            index,
            meta: getItemMeta(child, index),
        });
    });

    if (items.length < 2) return null;

    const onDragEnd = (e: DragEndEvent) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const from = items.findIndex((i) => i.id === active.id);
        const to = items.findIndex((i) => i.id === over.id);
        if (from === -1 || to === -1) return;
        moveChild(editor, getPos, node, from, to);
        onReorder?.();
    };

    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-xs text-muted-foreground">
                {t("lomda.scene-block.reorder", locale)}
            </span>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={onDragEnd}
            >
                <SortableContext
                    items={items.map((i) => i.id)}
                    strategy={verticalListSortingStrategy}
                >
                    <div className="flex flex-col gap-1">
                        {items.map((i) => (
                            <SortableRow
                                key={i.id}
                                id={i.id}
                                meta={i.meta}
                                onDelete={
                                    onDeleteItem
                                        ? () => onDeleteItem(i.index)
                                        : undefined
                                }
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    );
}
